import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useState } from "react";
import { useModalContext } from "../../context/ModalContext";
import { lookupTnMiddleware } from "../../middleware/lookupTnMiddleware";
import { LookupTnsInputDTO } from "../../model/input/lookupTnsInputDTO";
import { LookupTnService } from "../../model/store/lookupTnService";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { isValidQsPortalTn, isValidTn } from "../../utils/genericUtils";
import { FaCopy, FaDownload } from "react-icons/fa";
import { errorActions } from "../../store/errorSlice";
import {
  getEnabledQueryServices,
  isUserLawEnforcmentAgent,
} from "../../utils/authUtils";
import { LEA_TEXT } from "./leaText.json";
import { DATA } from "./dataDictionary.json";
import { useNavigate } from "react-router";
import { LookupTnResultInfo } from "./LookupTnResultInfo";
import { LookupTnExamples } from "./LookupTnExamples";
import { LookupTnDataGlossary } from "./LookupTnDataGlossary";
import { LookupTnResults } from "./LookupTnResults";

const getDropdownOptions = (
  authorizedOptions: LookupTnService[]
): { value: LookupTnService; display: string }[] => {
  let options: { value: LookupTnService; display: string }[] = [];

  if (authorizedOptions.includes("CID")) {
    options.push({ value: "CID", display: "Carrier ID" });
  }

  if (authorizedOptions.includes("NCHECK")) {
    options.push({ value: "NCHECK", display: "Number Check" });
  }

  if (authorizedOptions.includes("NVIEW")) {
    options.push({ value: "NVIEW", display: "Number View" });
  }

  return options;
};

const TN_SUBMIT_COUNT_LIMIT = 100;

const FORMATTING_HELP_MODAL_CONTENT = (
  <>
    <p>
      <div className="bold">Number must be submitted as</div>
      <ol>
        <li>- Country code and subscriber number including area code</li>
        <li>- No pluses, dashes, dots, or spaces. Only digits</li>
        <li>- No duplicates</li>
        <li>- No more than {TN_SUBMIT_COUNT_LIMIT} numbers</li>
        <li>
          - Use a new line (return key) for multiple TNs on the same request
        </li>
      </ol>
    </p>
    <div className="bold">Some Examples</div>
    <ol>
      <li>
        <div className="tn">12012000008</div>
      </li>
      <li>
        <div className="tn">19899850111</div>
      </li>
      <li>
        <div className="tn">332012049579</div>
      </li>
      <li>
        <div className="tn">492012049579</div>
      </li>
    </ol>
  </>
);

export interface QueryServicePageFormState {
  queryService: LookupTnService | "";
  tns: string;
  terms: boolean;
}

const INIT_FORM_STATE: QueryServicePageFormState = {
  queryService: "",
  tns: "",
  terms: false,
};

const INIT_FORM_ERROR: { [k in keyof QueryServicePageFormState]: string } = {
  queryService: "",
  tns: "",
  terms: "",
};

const QueryServicePage = () => {
  const [formState, setFormState] =
    useState<QueryServicePageFormState>(INIT_FORM_STATE);
  const [formErrorState, setFormError] = useState<{
    [k in keyof QueryServicePageFormState]: string;
  }>(INIT_FORM_ERROR);

  const { displayModal } = useModalContext();

  const dispatch = useAppDispatch();

  const isLea = useAppSelector((state) =>
    isUserLawEnforcmentAgent(state.auth.userProfile)
  );

  const queryServices = useAppSelector((state) =>
    getEnabledQueryServices(state.auth)
  );

  const updateFormState = (newState: QueryServicePageFormState): void => {
    setFormState((currState) => {
      return {
        ...currState,
        ...newState,
      };
    });
  };

  const showHelpModal = () => {
    displayModal(
      FORMATTING_HELP_MODAL_CONTENT,
      "Formatting Tips",
      { width: "50%" },
      true
    );
  };

  const submit = () => {
    let inputValidationError = false;

    const updateErrorState = (
      inputKey: keyof QueryServicePageFormState,
      value: string
    ) => {
      setFormError((currErrorState) => {
        return { ...currErrorState, [inputKey]: value };
      });
      inputValidationError = true;
    };

    // Clear any previous errors from previous submits
    setFormError(INIT_FORM_ERROR);

    if (!isLea && formState.queryService === "") {
      updateErrorState("queryService", "This value cannot be empty!");
    }

    if (formState.tns === "") {
      updateErrorState("tns", "This value cannot be empty!");
    }

    let tnList = formState.tns.split("\n");

    let invalidTns = tnList
      .map((tn) => {
        if (tn.trim() !== "" && !isValidQsPortalTn(tn)) {
          return tn;
        }
        return undefined;
      })
      .filter((data) => data !== undefined);

    if (invalidTns.length > 0) {
      updateErrorState(
        "tns",
        `You submitted invalid telephone numbers: ${invalidTns.join(",")}`
      );
    }

    if (tnList.length > TN_SUBMIT_COUNT_LIMIT) {
      updateErrorState(
        "tns",
        `You may not submit more than ${TN_SUBMIT_COUNT_LIMIT} telephone numbers`
      );
    }

    if (isLea && formState.terms === false) {
      updateErrorState("terms", `You must agree to the Terms and Conditions!`);
    }

    if (!inputValidationError) {
      setFormError(INIT_FORM_ERROR);

      let input: LookupTnsInputDTO = {
        tns: tnList
          .filter((tn) => tn.trim() !== "")
          .map((tn) => {
            return {
              tn: tn,
            };
          }),
        //@ts-ignore
        service: isLea ? "CID" : formState.queryService,
        lea_checkbox: isLea ? formState.terms : undefined,
        lea_agreement_version: isLea ? LEA_TEXT.version : undefined,
      };

      dispatch(lookupTnMiddleware(input));
    }
  };

  return (
    <>
      <Box className="lookup-tn-page-container">
        <Typography variant="h3">Number Lookup</Typography>
        <Paper className="lookup-tn-input-container" elevation={5}>
          <Box>
            <Grid container spacing={2}>
              {
                // =====================
                // Field #1: Select Type
                // =====================
              }
              {!isLea && (
                <>
                  <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                    <Typography variant="body1">
                      <span className="bold">Step 1:</span> Select a Query
                      Service
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 12, md: 8 }}>
                    {formErrorState.queryService !== "" ? (
                      <Box sx={{ color: "red" }}>
                        {formErrorState.queryService}
                      </Box>
                    ) : (
                      <></>
                    )}
                    <FormControl fullWidth>
                      <InputLabel id="query-type-select-label">
                        Query Service
                      </InputLabel>
                      <Select
                        labelId="query-type-select-label"
                        id="query-type-select"
                        label="Query Service"
                        value={formState.queryService}
                        error={formErrorState.queryService !== ""}
                        onChange={(e) => {
                          formState.queryService = e.target
                            .value as LookupTnService;
                          updateFormState(formState);
                        }}
                      >
                        {getDropdownOptions(queryServices).map((option) => {
                          return (
                            <MenuItem value={option.value}>
                              {option.display}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              {
                // =====================
                // Field #2: Number Input
                // =====================
              }
              <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                <Typography variant="body1">
                  <span className="bold">Step {isLea ? "1" : "2"}:</span> Enter
                  Telephone Numbers
                  {"  "}
                  <Link onClick={showHelpModal}>(E164 Formatting)</Link>
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 12, md: 8 }}>
                {formErrorState.tns !== "" ? (
                  <Box sx={{ color: "red" }}>{formErrorState.tns}</Box>
                ) : (
                  <></>
                )}
                <TextField
                  fullWidth
                  multiline
                  maxRows={20}
                  value={formState.tns}
                  id="outlined-basic"
                  label="Telephone Numbers"
                  variant="outlined"
                  error={formErrorState.tns !== ""}
                  onChange={(e) => {
                    formState.tns = e.target.value as string;
                    updateFormState(formState);
                  }}
                />
              </Grid>
              {
                // =====================
                // Field #3: LEA Check box
                // =====================
              }
              {isLea && (
                <>
                  <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                    <Typography variant="body1">
                      {" "}
                      <span className="bold"> Step 2:</span> Terms and
                      Conditions
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 12, md: 8 }}>
                    {formErrorState.terms !== "" ? (
                      <Box sx={{ color: "red" }}>{formErrorState.terms}</Box>
                    ) : (
                      <></>
                    )}
                    <FormGroup>
                      <FormControlLabel
                        required
                        control={
                          <Checkbox
                            value={formState.terms}
                            onChange={(e) => {
                              formState.terms = e.target.checked;
                              updateFormState(formState);
                            }}
                          />
                        }
                        label="I agree to the terms"
                      />
                    </FormGroup>
                  </Grid>
                </>
              )}
            </Grid>

            {
              // =====================
              // Action buttons
              // =====================
            }
            <Stack
              sx={{
                mt: 4,
                flexDirection: {
                  lg: "row",
                  md: "row",
                  sm: "column",
                  xs: "column",
                },
                justifyContent: { md: "end" },
              }}
            >
              <Button variant="contained" onClick={submit}>
                Submit
              </Button>
            </Stack>
            {
              // =====================
              // Legal Text
              // =====================
            }
            {isLea && (
              <Paper
                className="lookup-tn-legal-text"
                sx={{ bgcolor: "#f1f5f9" }}
              >
                <Box>
                  <div className="bold">* {LEA_TEXT.header}</div>
                  <p>
                    <p>{LEA_TEXT.body}</p>
                  </p>
                </Box>
              </Paper>
            )}
          </Box>
        </Paper>
        {
          // =====================
          // Results
          // =====================
        }
        <LookupTnResults formState={formState} />
        {/*
         * LEA Help Components
         */}
        {isLea ? (
          <>
            <LookupTnDataGlossary />
            <LookupTnResultInfo />
            <LookupTnExamples />
          </>
        ) : (
          <></>
        )}
      </Box>
    </>
  );
};

export default QueryServicePage;

// const LEA_OPTION_MODAL_CONTENT = (
//   <>
//     <p>
//       <div className="bold">By clicking the following checkbox, you:</div>
//     </p>
//     <p>
//       <p>
//         This Request Form is a written request for records or other information
//         that are relevant to a law enforcement agency’s investigation concerning
//         telemarketing fraud under 18 U.S.C. § 2703(c)(1)(D) or similar state
//         law. This written request is solely for the identity of the service
//         provider of a telephone number and does not include a request for text
//         messaging records. This subscriber or its customer is believed to be
//         engaged in telemarking, under 18 U.S.C. § 2325(1) or similar state law,
//         which includes a plan, program, promotion, or campaign that is conducted
//         to induce any of the following by use of one or more interstate
//         telephone calls, emails, text messages, or electronic instant messages
//         from a prospective purchaser or the manager of the plan, program,
//         promotion, or campaign: (1) purchase of goods or services; (2)
//         participation in a contest or sweepstakes; (3) a charitable
//         contribution, donation, or gift of money or any other thing of value;
//         (4) investment for financial profit; (5) participation in a business
//         opportunity; (6) commitment to a loan; or (7) participation in a
//         fraudulent medical study, research study, or pilot study. We request
//         that NetNumber, Inc. keep the issuance of this request confidential
//         until further notice. Any disclosure could impede an investigation and
//         thereby interfere with enforcement of the law.
//       </p>
//     </p>
//   </>
// );
